import { ProTable } from "@ant-design/pro-components";
import { ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "../../../assets/css/hTable.css";
import HButton from "../input/button/Button";

function throwRequiredParam(name) {
  throw new Error(`[HTable] Missing required prop: ${name}`);
}

export default function HTable({
  tableRef, // ref to control table actions (reload, etc.)
  tableContainerRef, // this container takes precedence over innerTableContainerRef if passed
  source = "", // source identifier for styling purposes
  locale = {}, // locale field is used to provide custom Empty text for each page if passed
  // ---- Grouped props (server-only) ----
  data = {}, // { request, requestExtraParams }
  ui = {}, // { title, columns, rowKey, search, options, sticky, height, width, hidePagination, fullScreen, manageColumns, reload, density, heightToBeSubtractedFromContainerHeight }
  toolbar = {}, // { onButtonClick, buttonText, buttonIcon, buttonPrefixIcon, buttonSuffixIcon, extra, render }
  behavior = {}, // { defaultPageSize, abortable }
  proProps = {}, // passthrough props to ProTable component
  // ---- Back-compat (flat) ----
  request: flatRequest, // legacy flat request prop
  columns: flatColumns, // legacy flat columns prop
  rowKey: flatRowKey, // legacy flat rowKey prop
  search: flatSearch, // legacy flat search prop
  title: flatTitle, // legacy flat title prop
  ...rest // additional props passed to ProTable
}) {
  // REQUIRED
  const columns = ui.columns || flatColumns || throwRequiredParam("columns");
  const rowKey = ui.rowKey || flatRowKey || throwRequiredParam("rowKey");
  const request = data.request || flatRequest || throwRequiredParam("request");
  const height = ui.height; // takes first priority over dynamicTableHeight
  const heightToBeSubtractedFromContainerHeight = ui.heightToBeSubtractedFromContainerHeight ||  0; // adjust the heightToBeSubtractedFromContainerHeight to perfectly fit for a table it will be adopted
  //  to all screens depending on its container height which is by default innerTableContainerRef
  // if you need to override this please use tableContainerRef to pass the height to be subtracted from the container height which is by default 150px
  const width = ui.width || 1000;

  const title = ui.title ?? flatTitle;
  const useNumberedPagination = ui.useNumberedPagination ?? false;
  const sticky = ui.sticky ?? true;
  const abortable = behavior.abortable !== false;
  const initialPageSize = ui.paginationProps?.pageSize || behavior.defaultPageSize || 20; // derive from numbered pagination props if provided
  const requestExtraParams = useMemo(() => data.requestExtraParams || {}, [data.requestExtraParams]);

  // Toolbar (left side, optional)
  const onButtonClick = toolbar.onButtonClick;
  const buttonText = toolbar.buttonText || "New";
  const buttonIcon = toolbar.buttonIcon || <div className="actionIconsSprite plusIconWhite" />;
  const buttonPrefixIcon = toolbar.buttonPrefixIcon;
  const buttonSuffixIcon = toolbar.buttonSuffixIcon;
  const extra = toolbar.extra || [];
  const customToolBarRender = toolbar.render;
  const buttonDataCy = toolbar.buttonDataCy || "hs-common-table-action-button";

  // Abort handling
  const aborterRef = useRef(null);
  const actionRef = useRef();

  // ---- Cursor pagination state ----
  const [cursor, setCursor] = useState(null); // current cursor (null = first page)
  const [pageNumber, setPageNumber] = useState(1); // shown at bottom-left
  const prevStackRef = useRef([]); // stack of previous cursors
  const nextCursorRef = useRef(null); // next cursor returned by API
  const [hasNext, setHasNext] = useState(false); // whether Next is clickable
  const hidePagination = ui.hidePagination || (!hasNext && pageNumber <= 1);
  const innerTableContainerRef = useRef(null);
  const footerRef = useRef(null);
  const [dynamicTableHeight, setDynamicTableHeight] = useState(620);
  const [isLoading, setIsLoading] = useState(true); // track loading state to prevent empty state flicker

  const [measuredHeights, setMeasuredHeights] = useState({
    footer: 0,
    toolbar: 0,
  });

  // Calculate table height based on measured values
  const calculateTableHeight = useCallback(() => {
    const containerElement = tableContainerRef?.current ?? innerTableContainerRef?.current;
    if (!containerElement) return 620; // default fallback

    const containerHeight = containerElement.offsetHeight;
    const { footer, toolbar } = measuredHeights;
    const availableHeight = containerHeight - heightToBeSubtractedFromContainerHeight - toolbar - footer - 10;
    return Math.max(400, availableHeight); // Minimum height of 400px
  }, [measuredHeights, heightToBeSubtractedFromContainerHeight, tableContainerRef]);

  useLayoutEffect(() => {
    const footerEl = document.querySelector(".hs-common-table .ant-table-footer");
    const toolbarEl = document.querySelector(".hs-common-table .ant-pro-table-list-toolbar");
    let rafId = null;
    const measureElements = () => {
      if (rafId) return; // Skip if already scheduled
    
      rafId = requestAnimationFrame(() => {
        const footerHeight = footerEl?.offsetHeight || 0;
        const toolbarHeight = toolbarEl?.offsetHeight || 0;
  
        setMeasuredHeights((prev) => {
          if (prev.footer !== footerHeight || prev.toolbar !== toolbarHeight) {
            return { footer: footerHeight, toolbar: toolbarHeight };
          }
          return prev;
        });
        
        rafId = null;
      });
    };

    measureElements();

    const newHeight = calculateTableHeight();
    setDynamicTableHeight(newHeight - measuredHeights.footer - 10);

    const tableContainer = document.querySelector(".hs-common-table .ant-table-container");
    if (tableContainer) {
      tableContainer.style.height = `${newHeight}px`;
    }

    const observer = new ResizeObserver(measureElements);
    footerEl && observer.observe(footerEl);
    toolbarEl && observer.observe(toolbarEl);
    return () => observer.disconnect();
  }, [calculateTableHeight, measuredHeights.footer]);

  // Compose request (cursor-based, abortable)
  const composedRequest = useMemo(() => {
    const base = async (params, sorter, filter, signal) => {
      setIsLoading(true);
      try {
        const pageSize = params?.pageSize || (useNumberedPagination ? initialPageSize : initialPageSize);
        const pageCursor = useNumberedPagination ? { page: Math.max(0, (params?.current || 1) - 1) } : cursor;

        const res = await request({
          cursor: pageCursor, // for numbered pagination we translate to page index
          limit: pageSize,
          sorter,
          filters: filter,
          signal,
          searchKeyword: params.keyword,
          ...requestExtraParams,
        });

        const { data, nextCursor, success, total } = res || {};
        nextCursorRef.current = nextCursor || null;

        // Backward compatible: Check for cursor field first (new format), then fall back to lastKey/lastRange
        if (!useNumberedPagination) {
          const shouldStopPagination = !nextCursor?.cursor && (!nextCursor?.lastKey || !nextCursor?.lastRange) && nextCursor?.page === undefined;
          setHasNext(!shouldStopPagination);
        }

        // ProTable needs { data, total, success }
        const result = {
          data: data || [],
          total: total ?? (data || []).length,
          success: !!success,
        };

        // Trigger fade-in animation when data is ready
        if (result.data && result.data.length > 0) {
          requestAnimationFrame(() => {
            const tableBody = innerTableContainerRef.current?.querySelector(".ant-table-tbody");
            if (tableBody) {
              tableBody.classList.remove("hs-common-table-fade-in");
              void tableBody.offsetWidth;
              tableBody.classList.add("hs-common-table-fade-in");
            }
          });
        }

        return result;
      } catch (error) {
        console.error("Error fetching data:", error);
        return { data: [], total: 0, success: false };
      } finally {
        setIsLoading(false);
      }
    };

    if (!abortable) return (p, s, f) => base(p, s, f);
    return (p, s, f) => {
      aborterRef.current?.abort?.();
      const controller = new AbortController();
      aborterRef.current = controller;
      return base(p, s, f, controller.signal);
    };
  }, [request, requestExtraParams, cursor, abortable]);

  // cleanup abort on unmount
  useEffect(() => () => aborterRef.current?.abort?.(), []);

  // Cursor actions (shown in footer for cursor-based pagination)
  const handlePrev = () => {
    if (pageNumber <= 1 || prevStackRef.current.length === 0) return;
    const prev = prevStackRef.current.pop() || null;
    setCursor(prev);
    setPageNumber((n) => Math.max(1, n - 1));

    reloadTable();
  };

  const reloadTable = () => {
    if (tableRef?.current?.reload) {
      tableRef?.current?.reload();
    } else {
      actionRef.current?.reload?.();
    }
  };

  const handleNext = () => {
    if (!nextCursorRef.current) return;
    prevStackRef.current.push(cursor); // push current, move to next
    setCursor(nextCursorRef.current);
    setPageNumber((n) => n + 1);
    reloadTable();
  };

  // Options (leave search out; ProTable uses top-level `search`)
  const options = {
    fullScreen: ui.fullScreen ?? false,
    setting: ui.manageColumns ?? false,
    reload: ui.reload ?? false,
    density: ui.density ?? false,
    search: ui.search
      ? {
          ...ui.search,
          className: `${ui.search.className} hs-w-250-px`, // Search UI styling
          placeholder: ui.search.placeholder || "Search",
        }
      : false,
  };
  // TOOLBAR: All items in the toolbar must have a height of 34px
  return (
    <ConfigProvider
      locale={enUS}
      theme={{
        components: {
          Table: {
            headerBg: "var(--hs-plain-white)",
            headerColor: "var(--hs-light-grey-2)",
            headerSplitColor: "transparent",
            colorText: "var(hs-color-BlackRussian)",
            fontSize: 14,
            fontWeightStrong: 400,
            footerBg: "var(--hs-plain-white)",
          },
        },
      }}
    >
      <div className="h-100 w-100" ref={innerTableContainerRef}>
        <ProTable
          locale={locale}
          scrollToFirstRowOnChange
          tableClassName="hs-common-table-inner"
          rootClassName="hs-common-table"
          sticky={sticky}
          className={`hs-border-6 w-100 hs-common-table ${source !== "h-table-with-tabs" ? "hs-border-md-grey" : ""}`}
          actionRef={tableRef || actionRef}
          headerTitle={title}
          columns={columns}
          rowKey={rowKey}
          search={false}
          virtual
          scroll={{ y: height ?? dynamicTableHeight, x: width }} // your fixed body height
          request={composedRequest}
          rowClassName={`${proProps.onRow ? "cursor-pointer" : ""} hs-common-table-row`}
          pagination={useNumberedPagination ? ui.paginationProps : false}
          loading={{
            loading: isLoading,
            indicator: (
              <div className="d-flex align-items-center justify-content-center">
                <div className="actionIconsSprite dropDownLoading position-absolute" />
              </div>
            ),
          }}
          toolBarRender={() => {
            const items = [...(extra || [])];

            if (onButtonClick) {
              items.push(
                <HButton
                  key="hs-common-table-action-button"
                  type="primary"
                  size="s"
                  icon={buttonIcon}
                  onClick={onButtonClick}
                  prefixIcon={buttonPrefixIcon}
                  suffixIcon={buttonSuffixIcon}
                  data-cy={buttonDataCy}
                >
                  {buttonText}
                </HButton>
              );
            }
            return typeof customToolBarRender === "function"
              ? customToolBarRender(items, { action: tableRef?.current || actionRef.current })
              : items;
          }}
          // Footer REPLACES number pagination area visually (only for cursor pagination)
          footer={() =>
            !useNumberedPagination &&
            !hidePagination && (
              <div ref={footerRef} id="hs-table-pagination-container" className={`d-flex justify-content-end align-items-center bg-white`}>
                <div className="hs-outline-grey d-flex hs-border-5 hs-h-30-px">
                  {pageNumber > 1 && (
                    <div
                      className="hs-w-30-px cursor-pointer hs-border-right-grey d-flex justify-content-center align-items-center"
                      onClick={handlePrev}
                    >
                      <div className="actionIconsSprite2 less-than-icon" />
                    </div>
                  )}
                  <div className="hs-w-30-px d-flex justify-content-center align-items-center">
                    <div id="page-number-container">{pageNumber}</div>
                  </div>
                  {hasNext && (
                    <div
                      className="hs-w-30-px cursor-pointer hs-border-left-grey d-flex justify-content-center align-items-center"
                      onClick={handleNext}
                    >
                      <div className="actionIconsSprite2 greater-than-icon" />
                    </div>
                  )}
                </div>
              </div>
            )
          }
          options={options}
          {...rest}
          {...proProps}
        />
      </div>
    </ConfigProvider>
  );
}
